// @ts-ignore
import CheckmarkBold from "svg/checkmark-bold.svg";
import Button from "components/Button";
import CollapsibleSection from "components/CollapsibleSection";
import { formatCurrency } from "helpers/currency";
import type { ArrangementPackage } from "./types";

type PackageCardProps = {
  pkg: ArrangementPackage;
  isSelected: boolean;
  onSelect: () => void;
  hideAction?: boolean;
  isPreSelected?: boolean;
};

export default function PackageCard({
  pkg,
  isSelected,
  onSelect,
  hideAction,
  isPreSelected = false,
}: PackageCardProps) {
  const { external } = pkg;

  // The "Pre-selected for you" badge takes precedence over the static
  // marketing badge ("Most popular", etc.) on the card the family arrived
  // pre-attached to — that information is more relevant than the marketing copy.
  const badgeText = isPreSelected ? "Pre-selected for you" : pkg.badge;
  const showBadge = Boolean(badgeText);

  return (
    <div className="relative">
      {isSelected && (
        <>
          <img
            src="public/After_Illustration_flowers_new2-1_funeral-guide.png"
            alt=""
            className="pointer-events-none absolute -top-[90px] left-[10px] z-0 w-2/5 rotate-90"
          />
          <img
            src="public/After_Illustration_flowers_reviews-right.webp"
            alt=""
            className="pointer-events-none absolute -bottom-[120px] right-[30px] z-0 w-2/5 -rotate-90"
          />
        </>
      )}
      <div
        className={`relative z-10 flex flex-col rounded-md border-2 px-6 pb-6 ${
          showBadge ? "pt-8" : "pt-6"
        } ${isSelected ? "border-evergreen bg-evergreen" : "border-oat bg-oat"}`}
      >
        {showBadge && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <span
              className={`whitespace-nowrap rounded-full px-5 py-2 text-xs font-bold ${
                isSelected
                  ? "border-2 border-white bg-soil text-white"
                  : "border-2 border-white bg-oat text-soil"
              }`}
            >
              {badgeText}
            </span>
          </div>
        )}

        <div className="flex-1 space-y-4">
          {/* Name and Price */}
          <div className="flex items-end justify-between gap-2">
            <span
              className={`font-serif text-3xl italic ${isSelected ? "text-ivory" : "text-evergreen"}`}
            >
              {pkg.title}
            </span>
            <div className="text-right">
              <span
                className={`block text-xs ${isSelected ? "text-oat" : "text-soil"}`}
              >
                Starting at
              </span>
              <span
                className={`text-3xl font-bold ${isSelected ? "text-daisy" : "text-evergreen"}`}
              >
                {formatCurrency(parseFloat(pkg.price))}
              </span>
            </div>
          </div>

          {/* Subtitle and tagline */}
          <div>
            <p
              className={`text-sm font-bold ${isSelected ? "text-oat" : "text-soil"}`}
            >
              {external.subtitle}
            </p>
            {external.tagline && (
              <p
                className={`mt-1 text-sm ${isSelected ? "text-oat" : "text-soil"}`}
              >
                {external.tagline}
              </p>
            )}
          </div>

          {/* Collapsible sections */}
          <div className="space-y-2">
            {external.why_choose_detail && (
              <CollapsibleSection
                title={`Why families choose ${pkg.title}`}
                textColorClass={isSelected ? "text-oat" : "text-evergreen"}
              >
                <p
                  className={`text-sm ${isSelected ? "text-oat" : "text-soil"}`}
                >
                  {external.why_choose_detail}
                </p>
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title="What's included"
              textColorClass={isSelected ? "text-oat" : "text-evergreen"}
            >
              <ul
                className={`space-y-3 text-sm ${isSelected ? "text-oat" : "text-evergreen"}`}
              >
                {external.inclusion_highlights.map((item) => (
                  <li
                    key={item.title}
                    className={
                      item.emphasize
                        ? `font-bold ${isSelected ? "text-oat" : "text-soil"}`
                        : ""
                    }
                  >
                    {item.title}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>

            {external.exclusion_highlights.length > 0 && (
              <CollapsibleSection
                title="Does not include"
                textColorClass={isSelected ? "text-oat" : "text-evergreen"}
              >
                <ul
                  className={`space-y-3 text-sm ${isSelected ? "text-oat" : "text-soil"}`}
                >
                  {external.exclusion_highlights.map((item) => (
                    <li key={item.title}>{item.title}</li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}
          </div>
        </div>

        <div className="mt-4">
          {isSelected ? (
            <div className="flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 font-bold text-daisy">
              <span className="h-4 w-4">
                <CheckmarkBold />
              </span>
              Selected
            </div>
          ) : hideAction ? null : (
            <Button onClick={onSelect} variant="secondary" fullWidth>
              Choose {pkg.title}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
